// eslint-disable-next-line vue/first-attribute-linebreak
import * as Tone from 'tone';
import { BarsBeatsSixteenths } from 'tone/build/esm/core/type/Units';

import { onMounted, onUnmounted, Ref, ref, reactive, computed, ComputedRef, watch } from 'vue';
import { throttle } from './debounce';
import { getUrl } from './url';
import { AudioEnergy, useDetectBeat } from '@/utils/AudioEnergy';

const DEFAULT_MUSIC_DURATION = 109;
Tone.Transport.bpm.value = 124;
Tone.Transport.loop = true;
Tone.Transport.loopStart = 0;
Tone.Transport.debug = true;

export type synthMap<T> = {
    A: Array<T>,
    // B: Array<T>,
    // C: Array<T>,
    // D: Array<T>,
}

export const audioMap: synthMap<number> = {
    A: [49, 50, 51, 52, 53, 54, 55, 56], // 1 ~ 8, 57, 48,
    // B: [81, 87, 69, 82, 84, 89, 85, 73, 79, ], // q~o
    // C: [65, 83, 68, 70, 71, 72, 74, 75, ], // a~k
    // D: [90, 88, 67, 86, 66, 78, 77, 188, 190, ], // z~m
};

export type GrpType = 'A';

export const keyCode2GrpMap: { [key: string]: GrpType } = {};
Object.entries(audioMap).forEach(([key, value]) => {
    value.forEach((item) => {
        keyCode2GrpMap[item] = key as GrpType;
    });
});

export type SynthInfo = {k: number, n: string, e: 0 | 1, s: 0 | 1, pk?: string, fd?: 0 | 1, name: string }

export const defaultKeyMap: synthMap<SynthInfo> = {
    A: [{ k: 49, n: '1', e: 1, s: 0, name: 'Original' },
        { k: 50, n: '2', e: 1, s: 0, name: 'Color' },
        { k: 51, n: '3', e: 1, s: 0, name: '8-bit' },
        { k: 52, n: '4', e: 1, s: 0, name: 'Pendulum' }],
};

enum MddEventEnum {
    OnStage = 'on-stage',
    OnPlay = 'on-play',
    OnBeat = 'on-beat'
}
class MddEvent<T> extends EventTarget {
    private name = '';
    constructor(name: string) {
        super();
        this.name = name;
    }

    addHandler(callback: (evt: Event) => void) {
        this.addEventListener(this.name, callback);
    }

    emit(stageIndex: T) {
        this.dispatchEvent(new CustomEvent(this.name, { detail: stageIndex }));
    }
}

function getDefaultToneList() {
    const toneStateLoaded = reactive<{[key: string]: boolean }>({
        a0: false,
        a1: false,
        a2: false,
        a3: false,
    });
    const toneList = new Array(4).fill(0).map((_, idx) => new Tone.Player({
        url: getUrl(`audio/a${idx}.mp3`),
        loop: true,
        volume: 0.9,
        mute: true,
        onload() {
            toneStateLoaded[`a${idx}`] = true;
        },
    }).toDestination().sync().start(0));
    const keyMap = defaultKeyMap;
    return {
        keyMap,
        toneStateLoaded,
        toneList,
        duration: DEFAULT_MUSIC_DURATION,
    };
}

export function useMdd(toneFunc = getDefaultToneList) {
    // const keyMap = defaultKeyMap;
    const playEvent = new MddEvent<number>(MddEventEnum.OnPlay);
    const beatEvent = new MddEvent<boolean>(MddEventEnum.OnBeat);
    const {
        keyMap,
        toneStateLoaded,
        toneList,
        duration,
    } = toneFunc();
    Tone.Transport.loopEnd = duration;

    const isLoaded = computed(() => Object.values(toneStateLoaded).every(item => item));
    const isPlaying = ref(Tone.Transport.state === 'started');
    const analyser = new AudioEnergy();

    const transportState: {
        intervalId: number,
        position: BarsBeatsSixteenths,
    } = {
        intervalId: 0,
        position: '0:0:0',
    };
    const stageSecTrigger = [0, 30, 64, 80];
    const { start: stageStart, stop: stageStop, reset: stageReset, stageEvent } = useStageTrigger(stageSecTrigger, duration);

    const currentIdx = ref(-1);
    const playSynth = throttle((idx: number) => {
        if (!isLoaded.value || !isPlaying.value) return;
        if (currentIdx.value === idx) return;
        toneList.forEach((synth, index) => {
            if (index === idx) {
                synth.mute = false;
            } else {
                synth.mute = true;
            }
        });
        toneList[currentIdx.value] && toneList[currentIdx.value].disconnect(analyser);
        toneList[idx].connect(analyser);
        currentIdx.value = idx;
        playEvent.emit(idx);
    }, 400, { start: true, middle: false, once: false });

    const toggleStart = () => {
        if (currentIdx.value === -1) {
            start(0);
        } else {
            isPlaying.value ? stopAll() : start(currentIdx.value);
        }
        return isPlaying.value;
    };

    onMounted(() => {
        try {
            Tone.start();
        } catch (error) {
            console.error('error', error);
        }
        window.addEventListener('keydown', keypressHandler);
    });

    onUnmounted(() => {
        window.removeEventListener('keydown', keypressHandler);
        stopAll();
    });

    const { detectBeat } = useDetectBeat();
    function start(synthId: number) {
        if (!isPlaying.value) {
            isPlaying.value = true;
            clearInterval(transportState.intervalId);
            playSynth(synthId);
            Tone.Transport.start('+0', transportState.position);
            transportState.intervalId = setInterval(() => {
                transportState.position = Tone.Transport.position as BarsBeatsSixteenths;
                // const time = Tone.TransportTime(transportState.progress, 's');
                // scale it between 0-1
                // for (let index = 0; index < toneList.length; index++) {
                //   const synth = toneList[index];
                //   console.log(`sec: ${index}`, synth.toSeconds());
                // }
                // transportState.progress = Tone.Transport.progress;
                // console.log('progress', Tone.Transport.progress);
                analyser.update();
                if (detectBeat(analyser.getEnergy('highMid'))) {
                    beatEvent.emit(true);
                }
                // console.log('energy', energy);
            }, 16) as unknown as number;
            stageStart();
        }
    }

    function stopAll() {
        if (isPlaying.value) {
            clearInterval(transportState.intervalId);
            stageStop();
            Tone.Transport.stop();
            isPlaying.value = false;
        }
    }

    function keypressHandler(event: KeyboardEvent) {
        const keyCode = event?.keyCode;
        if (keyCode === 32) {
            event.preventDefault();
            toggleStart();
            return;
        }
        const key = keyCode2GrpMap[keyCode];
        if (key && audioMap[key]) {
            const index = audioMap[key].indexOf(keyCode);

            if (index >= 0) {
                const km = keyMap[key];
                if (!km[index]?.e) return;
                playSynth(index);
            }
        }
    }
    return {
        analyser,
        stageEvent,
        beatEvent,
        playEvent,
        transportState,
        currentIdx,
        toggleStart,
        start,
        playSynth,
        stopAll,
        isPlaying,
        // keypressHandler,
        isLoaded,
        keyMap,
    };
}

function useStageTrigger(_stageSecTrigger: number[], endSecond: number, time = 500) {
    const stageSecTrigger = _stageSecTrigger.map(item => item * 1000);
    let state = {
        intervalId: 0,
        nextIndex: 0,
        time: 0,
        maxIndex: stageSecTrigger.length - 1,
        endTime: endSecond * 1000,
    };
    const stageEvent = new MddEvent(MddEventEnum.OnStage);

    const reset = () => {
        clearInterval(state.intervalId);
        state = {
            intervalId: 0,
            nextIndex: 0,
            time: 0,
            maxIndex: stageSecTrigger.length - 1,
            endTime: endSecond * 1000,
        };
    };
    const stop = () => {
        clearInterval(state.intervalId);
        state.intervalId = 0;
    };

    const start = () => {
        if (state.intervalId > 0) return;
        handler();
        state.intervalId = setInterval(() => {
            handler();
        }, time) as unknown as number;
    };

    function handler() {
        if (state.time >= stageSecTrigger[state.nextIndex] && state.nextIndex <= state.maxIndex) {
            stageEvent.emit(state.nextIndex);
            state.nextIndex++;
        }
        if (process.env.IS_DEV) {
            console.log(state.time);
        }
        if (state.time >= state.endTime) { // onLoop
            reset();
            start();
        }
        state.time += time;
    }

    return {
        stageEvent,
        state,
        start,
        stop,
        reset,
    };
}
